import { Component } from '@theme/component';

/**
 * `<bundle-addon>` — the active group's optional add-on for one step.
 *
 * The add-on is GROUP-scoped: when the shopper switches groups the builder
 * resets `addOnSelected` and the offer itself changes. So on every
 * `bundle:update` this component re-reads the active group's `addOn` from the
 * builder, shows/hides itself, repopulates the image / title / price, and
 * reflects the checked state.
 *
 * The checkbox binds to `bundle-builder/toggleAddOn?step=N`.
 *
 * Contract owned: `data-step`, refs image / title / price, `ref="check"`.
 */
export class BundleAddonComponent extends Component {
  #builder = null;
  #onUpdate = null;

  connectedCallback() {
    super.connectedCallback();
    this.stepIndex = Number(this.dataset.step);
    this.#builder = this.closest('bundle-builder');
    this.#onUpdate = this.#handleUpdate.bind(this);
    this.#builder?.addEventListener('bundle:update', this.#onUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#onUpdate) this.#builder?.removeEventListener('bundle:update', this.#onUpdate);
  }

  #handleUpdate() {
    const builder = this.#builder;
    if (!builder) return;
    // Read the DRAFT — the add-on is edited in-step and only commits on Save.
    const sel = builder.getDraftSelection(this.stepIndex);
    if (!sel) return;

    const addOn = sel.group?.addOn ?? null;
    // No add-on for the active group → hide the whole block.
    this.toggleAttribute('hidden', !addOn);
    if (!addOn) return;

    if (this.refs.title) this.refs.title.textContent = addOn.title ? `Add ${addOn.title}` : '';
    if (this.refs.price) {
      this.refs.price.textContent = addOn.price ? `From ${builder.money(addOn.price).replace('.00', '')}` : '';
    }

    // Keep the checkbox visually in sync with the (possibly reset) state.
    const check = this.#checkbox();
    if (check) check.checked = !!sel.state.addOnSelected;
  }

  #checkbox() {
    return (
      this.refs.check ||
      /** @type {HTMLInputElement|null} */ (this.querySelector('input[type="checkbox"]'))
    );
  }
}

if (!customElements.get('bundle-addon')) {
  customElements.define('bundle-addon', BundleAddonComponent);
}
